import type { GameplayDebugSnapshot } from "../../game/debugSnapshot";
import type { DebugSettings } from "../../game/debugSettings";
import {
  buildDebugOverlaySections,
  mFont,
  roundedRectPath,
} from "../renderConstants";

// ── Dependency interface ────────────────────────────────────────────

export interface DebugOverlayDeps {
  ctx: CanvasRenderingContext2D;
  canvasWidth: number;
  canvasHeight: number;
  dpr: number;
}

// ── Extracted screen function ───────────────────────────────────────

export function drawDebugOverlayScreen(
  deps: DebugOverlayDeps,
  debugViewModel: GameplayDebugSnapshot,
  debugSettings: DebugSettings,
): void {
  const { ctx, canvasWidth, canvasHeight, dpr } = deps;
  const viewport = {
    width: canvasWidth / dpr,
    height: canvasHeight / dpr,
  };
  const sections = buildDebugOverlaySections(debugViewModel, viewport);
  let gameplayLines = debugSettings.overlayGameplay ? [...sections.gameplay] : [];
  let botLines = debugSettings.overlayBot ? [...sections.bot] : [];

  if (debugSettings.diagnostics === "OFF") {
    gameplayLines = gameplayLines.filter((line) => line.startsWith("PHS") || line.startsWith("DOOM") || line.startsWith("SCR"));
    botLines = [];
  } else if (debugSettings.diagnostics === "CORE") {
    gameplayLines = gameplayLines.filter((line) => !line.startsWith("ANM"));
  }

  if (debugSettings.perfSafeMode) {
    gameplayLines = gameplayLines.slice(0, 3);
    botLines = botLines.slice(0, 1);
  } else if (debugSettings.verbosity === "STANDARD") {
    gameplayLines = gameplayLines.slice(0, sections.compact ? 3 : 4);
    botLines = botLines.slice(0, sections.compact ? 1 : 2);
  }

  if (gameplayLines.length === 0 && botLines.length === 0) {
    return;
  }

  const lineHeight = sections.compact ? 10 : 11;
  const headerHeight = 14;
  const lines = gameplayLines.length + botLines.length;
  const panelHeight = headerHeight + lines * lineHeight + 8;
  const panelWidth = sections.compact ? 206 : 252;
  const panelX = viewport.width - panelWidth - 8;
  const panelY = 8;

  ctx.save();
  roundedRectPath(ctx, panelX, panelY, panelWidth, panelHeight, 5);
  ctx.fillStyle = "rgba(6,10,20,0.84)";
  ctx.fill();
  roundedRectPath(ctx, panelX, panelY, panelWidth, panelHeight, 5);
  ctx.strokeStyle = "rgba(124,168,234,0.9)";
  ctx.lineWidth = 1;
  ctx.stroke();

  ctx.fillStyle = "#b4cdf5";
  ctx.font = mFont("bold", 6);
  ctx.textAlign = "left";
  ctx.textBaseline = "top";
  ctx.fillText(sections.compact ? "DEBUG" : "DEBUG OVERLAY", panelX + 8, panelY + 5);

  let y = panelY + headerHeight;
  ctx.fillStyle = "#d9e9ff";
  for (const line of gameplayLines) {
    ctx.fillText(line, panelX + 8, y);
    y += lineHeight;
  }

  if (botLines.length > 0) {
    ctx.fillStyle = "#8be3be";
    for (const line of botLines) {
      ctx.fillText(line, panelX + 8, y);
      y += lineHeight;
    }
  }
  ctx.restore();
}
